<template>
  <div class="container login login-container-sm">
    <div class="row">
      <div class="login-form mw-100">
        <h2 class="text-center">{{ t("LOG_IN") }}</h2>

        <div class="header-separator">
          <div class="separator-line"></div>
        </div>

        <Tabs v-model:value="activeTab" class="login-tabs">
          <TabList>
            <Tab value="0" class="w-50">{{ t('IDENTIFIER') }}</Tab>
            <Tab value="1" class="w-50">{{ t('PASSWORD') }}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <Button class="enter-btn" :label="t('ENTER')" @click="login" autofocus/>
            </TabPanel>

            <TabPanel value="1">
              <div v-if="!showResetForm">
                <form @submit.prevent="passwordLogin">
                  <div class="row">
                    <div class="col">
                      <div class="input-group">
                        <InputText
                            type="text"
                            name="username"
                            :placeholder="t('SSN')"
                            class="mb-2 login-input flex-grow-1"
                            v-model="creds.username"
                            @input="onUsernameInput"
                        />
                      </div>

                      <div class="input-group">
                        <InputText
                            :placeholder="t('PASSWORD')"
                            :type="!passwordHidden ? 'password' : 'text'"
                            class="login-input flex-grow-1"
                            v-model="creds.password"
                        />
                        <span v-if="creds.password !== ''" class="input-group-text" style="cursor: pointer" @click="toggleShowPassword">
                          <i :class="passwordHidden ? 'pi pi-eye-slash' : 'pi pi-eye'"/>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="row mt-3 error-message text-center">
                    <div class="col" v-if="!activatedAccount">
                      {{ t("NOT_ACTIVE_ACCOUNT2") }}
                    </div>
                  </div>

                  <div class="row mb-2">
                    <div class="col">
                      <Button class="enter-btn" type="submit" :label="t('ENTER')"/>
                    </div>
                  </div>
                </form>

                <div class="row">
                  <div class="col text-center">
                    <a href="#" class="text16-blue" @click.prevent="showResetForm = true">{{ t("FORGOT_PASSWORD") }}</a>
                  </div>
                </div>
              </div>

              <reset-password :showResetForm="showResetForm" @hide-form="resetPasswordHandler"/>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <div class="header-separator">
          <div class="separator-line"></div>
        </div>

        <div class="col text-center">
          <div>{{ t("NO_ACCOUNT2") }}</div>
          <div>
            <router-link to="/login/register">{{ t("REGISTER_HERE") }}</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ResetPassword from "@/views/components/ResetPassword.vue";
import {useI18n} from "vue-i18n";
import {onMounted, ref} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useRoute} from "vue-router";
import {setRedirectPath} from "@/auth/redirect";
import {useToast} from "@/util/toastUtils.js";
import {Language} from "@/model/language";

const {t, locale} = useI18n();
const auth = injectAuth();
const axios = injectAxios();
const route = useRoute();
const toast = useToast();

const showResetForm = ref(false);
const creds = ref({
  username: "",
  password: "",
});
const oAuthEndpoint = import.meta.env.VITE_OAUTH_ENDPOINT;
const activatedAccount = ref(true);
const passwordHidden = ref(false);
const activeTab = ref("0");

onMounted(() => {
  setRedirectPath(route.query.next);
});

const resetPasswordHandler = () => {
  showResetForm.value = false
}

function toggleShowPassword() {
  passwordHidden.value = !passwordHidden.value;
}

function onUsernameInput() {
  creds.value.username = (creds.value.username ?? '').replaceAll(/\s/g, '');
}

async function passwordLogin() {
  try {
    const response = await axios.post("/login/plain", creds.value);
    await auth.login(response.data.token);
  } catch (e) {
    if (e.response.data.message === "NOT_ACTIVATED_ACCOUNT") {
      activatedAccount.value = false;
    }
  }

  creds.value.password = "";
}

const getLocale = () => {
  switch (String(locale.value)) {
    case Language.EST:
      return "et";
    case Language.RUS:
      return "ru";
    default:
      return "en";
  }
};

const getOAuthURL = () => {
  return `${oAuthEndpoint}?time=${Date.now()}&locale=${getLocale()}`;
};

const login = () => {
  location.href = getOAuthURL();
};
</script>

<style scoped>
.login {
  margin-top: 64px;
}

.login-container-sm {
  display: flex;
  justify-content: center;
}

.login-tabs {
  max-width: 720px;
}

@media screen and (min-width: 575px) {
  .login-tabs {
    min-width: 575px;
  }
}

.login-form {
  display: flex;
  flex-direction: column;
  margin: 40px 0;
}

.error-message {
  color: #ff0000;
}
</style>

<style>
.login-tabs ul > li {
  width: 50%;

  & > a {
    justify-content: center;
  }
}
</style>
